import { useEffect, useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import type { ColumnDef } from '@tanstack/react-table';
import { Chip } from '@weareopenr/component-library';
import { DataTable } from '../../components/DataTable/DataTable';
import {
  useAllInvoices,
  useBillingProfile,
  useGetTenants,
  usePermissions,
  useSaveBillingProfile,
} from '../../hooks';
import type { IBillingProfile, IInvoice } from '../../types';
import { fmtMoney, shortDate, statusColour, statusLabel } from './format';
import styles from './Invoices.module.scss';

type Filter = 'all' | 'outstanding' | 'overdue' | 'draft' | 'paid';

const FILTERS: { id: Filter; label: string }[] = [
  { id: 'all', label: 'All' },
  { id: 'outstanding', label: 'Outstanding' },
  { id: 'overdue', label: 'Overdue' },
  { id: 'draft', label: 'Drafts' },
  { id: 'paid', label: 'Paid' },
];

const matches = (invoice: IInvoice, filter: Filter): boolean => {
  if (filter === 'all') return true;
  if (filter === 'outstanding') return invoice.status === 'issued' || invoice.status === 'sent';
  if (filter === 'overdue') return invoice.overdue;
  return invoice.status === filter;
};

type ProfileDraft = Omit<
  IBillingProfile,
  'updated_at' | 'updated_by' | 'address_lines' | 'email_cc'
> & {
  address_lines: string;
  email_cc: string;
};

const toDraft = (profile: IBillingProfile): ProfileDraft => {
  const { updated_at: _a, updated_by: _b, ...rest } = profile;
  return {
    ...rest,
    address_lines: profile.address_lines.join('\n'),
    email_cc: profile.email_cc.join(', '),
  };
};

const str = (value: string | number | null) => (value === null ? '' : String(value));
const NO_ROWS: never[] = [];

const CompanyDetails = () => {
  const { data: profile, isLoading } = useBillingProfile();
  const save = useSaveBillingProfile();
  const { canBill } = usePermissions();
  const [editing, setEditing] = useState(false);
  const [draft, setDraft] = useState<ProfileDraft | null>(null);
  useEffect(() => {
    if (profile) setDraft(toDraft(profile));
  }, [profile]);

  if (isLoading || !profile || !draft)
    return <p className={styles.muted}>Loading company details…</p>;
  const set = <K extends keyof ProfileDraft>(key: K, value: ProfileDraft[K]) =>
    setDraft((current) => (current ? { ...current, [key]: value } : current));
  const error = save.error as { message?: string } | null;

  if (!editing) {
    return (
      <section className={styles.panel} aria-label="Company details">
        <header className={styles.head}>
          <div>
            <h2 className={styles.title}>Company details on invoices</h2>
            <p className={styles.muted}>Printed on every invoice and used as the sender.</p>
          </div>
          {canBill ? (
            <button type="button" className={styles.ghost} onClick={() => setEditing(true)}>
              Edit
            </button>
          ) : null}
        </header>
        <div className={styles.grid}>
          <div>
            <strong>{profile.company_name}</strong>
            {profile.address_lines.map((line) => (
              <div key={line}>{line}</div>
            ))}
            {profile.phone ? <div>Telephone {profile.phone}</div> : null}
          </div>
          <div>
            <div>From {profile.email_from}</div>
            {profile.email_cc.length ? <div>Cc {profile.email_cc.join(', ')}</div> : null}
            {profile.email_reply_to ? <div>Reply to {profile.email_reply_to}</div> : null}
            <div>
              Terms net {profile.payment_terms_days} days, VAT {profile.vat_rate}%
            </div>
            <div>
              Next number{' '}
              <code>
                {profile.number_prefix}
                {profile.next_invoice_number}
              </code>
            </div>
          </div>
          <div>
            <div>{profile.bank_name ?? 'No bank'}</div>
            <div>
              {profile.sort_code ?? ''} {profile.account_number ?? ''}
            </div>
            <div>{profile.iban ?? ''}</div>
            <div>{profile.swift ?? ''}</div>
          </div>
          <div>
            <div>VAT {profile.vat_number ?? <span className={styles.warn}>not set</span>}</div>
            <div>
              Company no {profile.company_number ?? <span className={styles.warn}>not set</span>}
            </div>
            <div className={styles.small}>{profile.registered_office}</div>
          </div>
        </div>
      </section>
    );
  }

  return (
    <section className={styles.panel} aria-label="Company details">
      <header className={styles.head}>
        <h2 className={styles.title}>Company details on invoices</h2>
      </header>
      <form
        className={styles.form}
        onSubmit={(event) => {
          event.preventDefault();
          save.mutate(
            {
              ...draft,
              address_lines: draft.address_lines
                .split('\n')
                .map((line) => line.trim())
                .filter(Boolean),
              email_cc: draft.email_cc
                .split(',')
                .map((item) => item.trim())
                .filter(Boolean),
              phone: draft.phone || null,
              email_reply_to: draft.email_reply_to || null,
              vat_number: draft.vat_number || null,
              company_number: draft.company_number || null,
              registered_office: draft.registered_office || null,
              bank_name: draft.bank_name || null,
              sort_code: draft.sort_code || null,
              account_number: draft.account_number || null,
              iban: draft.iban || null,
              swift: draft.swift || null,
              late_payment_note: draft.late_payment_note || null,
            },
            { onSuccess: () => setEditing(false) },
          );
        }}
      >
        <div className={styles.row3}>
          <label className={styles.field}>
            <span>Company name</span>
            <input
              value={draft.company_name}
              onChange={(event) => set('company_name', event.target.value)}
              required
            />
          </label>
          <label className={styles.field}>
            <span>Telephone</span>
            <input
              value={str(draft.phone)}
              onChange={(event) => set('phone', event.target.value)}
            />
          </label>
          <label className={styles.field}>
            <span>Currency</span>
            <input
              value={draft.currency}
              onChange={(event) => set('currency', event.target.value.toUpperCase())}
            />
          </label>
        </div>
        <label className={styles.field}>
          <span>Address (one line per row)</span>
          <textarea
            rows={4}
            value={draft.address_lines}
            onChange={(event) => set('address_lines', event.target.value)}
          />
        </label>
        <div className={styles.row3}>
          <label className={styles.field}>
            <span>Send from</span>
            <input
              type="email"
              value={draft.email_from}
              onChange={(event) => set('email_from', event.target.value)}
              required
            />
            <small>Must be on a domain verified in SES.</small>
          </label>
          <label className={styles.field}>
            <span>Always cc</span>
            <input
              value={draft.email_cc}
              onChange={(event) => set('email_cc', event.target.value)}
              placeholder="finance@weareopenr.com"
            />
          </label>
          <label className={styles.field}>
            <span>Reply to</span>
            <input
              type="email"
              value={str(draft.email_reply_to)}
              onChange={(event) => set('email_reply_to', event.target.value)}
            />
          </label>
        </div>
        <div className={styles.row3}>
          <label className={styles.field}>
            <span>VAT number</span>
            <input
              value={str(draft.vat_number)}
              onChange={(event) => set('vat_number', event.target.value)}
              placeholder="GB 123 4567 89"
            />
          </label>
          <label className={styles.field}>
            <span>Company number</span>
            <input
              value={str(draft.company_number)}
              onChange={(event) => set('company_number', event.target.value)}
            />
          </label>
          <label className={styles.field}>
            <span>Default VAT rate (%)</span>
            <input
              type="number"
              min={0}
              max={100}
              step="0.1"
              value={draft.vat_rate}
              onChange={(event) => set('vat_rate', Number(event.target.value))}
            />
          </label>
        </div>
        <label className={styles.field}>
          <span>Registered office</span>
          <input
            value={str(draft.registered_office)}
            onChange={(event) => set('registered_office', event.target.value)}
          />
        </label>
        <div className={styles.row3}>
          <label className={styles.field}>
            <span>Bank</span>
            <input
              value={str(draft.bank_name)}
              onChange={(event) => set('bank_name', event.target.value)}
            />
          </label>
          <label className={styles.field}>
            <span>Sort code</span>
            <input
              value={str(draft.sort_code)}
              onChange={(event) => set('sort_code', event.target.value)}
            />
          </label>
          <label className={styles.field}>
            <span>Account number</span>
            <input
              value={str(draft.account_number)}
              onChange={(event) => set('account_number', event.target.value)}
            />
          </label>
        </div>
        <div className={styles.row3}>
          <label className={styles.field}>
            <span>IBAN</span>
            <input value={str(draft.iban)} onChange={(event) => set('iban', event.target.value)} />
          </label>
          <label className={styles.field}>
            <span>SWIFT</span>
            <input
              value={str(draft.swift)}
              onChange={(event) => set('swift', event.target.value)}
            />
          </label>
          <label className={styles.field}>
            <span>Default payment terms (days)</span>
            <input
              type="number"
              min={0}
              value={draft.payment_terms_days}
              onChange={(event) => set('payment_terms_days', Number(event.target.value))}
            />
          </label>
        </div>
        <div className={styles.row3}>
          <label className={styles.field}>
            <span>Invoice number prefix</span>
            <input
              value={draft.number_prefix}
              onChange={(event) => set('number_prefix', event.target.value)}
              placeholder="none"
            />
          </label>
          <label className={styles.field}>
            <span>Next invoice number</span>
            <input
              type="number"
              min={1}
              value={draft.next_invoice_number}
              onChange={(event) => set('next_invoice_number', Number(event.target.value))}
            />
            <small>Can only move forward.</small>
          </label>
          <label className={styles.field}>
            <span>Late payment note</span>
            <input
              value={str(draft.late_payment_note)}
              onChange={(event) => set('late_payment_note', event.target.value)}
            />
          </label>
        </div>
        {error ? (
          <p className={styles.error} role="alert">
            {error.message ?? 'Could not save'}
          </p>
        ) : null}
        <div className={styles.actions}>
          <button type="button" className={styles.ghost} onClick={() => setEditing(false)}>
            Cancel
          </button>
          <button type="submit" className={styles.primary} disabled={save.isLoading}>
            {save.isLoading ? 'Saving…' : 'Save company details'}
          </button>
        </div>
      </form>
    </section>
  );
};

export const Invoices = () => {
  const navigate = useNavigate();
  const { data: invoices = NO_ROWS, isLoading, error } = useAllInvoices();
  const { data: tenants = NO_ROWS } = useGetTenants();
  const [filter, setFilter] = useState<Filter>('outstanding');
  const [search, setSearch] = useState('');
  const tenantName = useMemo(
    () => new Map(tenants.map((tenant) => [tenant.name, tenant.display_name])),
    [tenants],
  );
  const rows = useMemo(
    () => invoices.filter((invoice) => matches(invoice, filter)),
    [invoices, filter],
  );

  const outstanding = invoices.filter(
    (invoice) => invoice.status === 'issued' || invoice.status === 'sent',
  );
  const overdue = outstanding.filter((invoice) => invoice.overdue);
  const owed = outstanding.reduce((sum, invoice) => sum + invoice.total, 0);
  const overdueOwed = overdue.reduce((sum, invoice) => sum + invoice.total, 0);

  const columns = useMemo<ColumnDef<IInvoice, any>[]>(
    () => [
      {
        header: 'Number',
        accessorKey: 'number',
        size: 90,
        cell: ({ getValue }) => <strong>{getValue<string>()}</strong>,
      },
      {
        id: 'tenant',
        header: 'Tenant',
        accessorFn: (row) => `${tenantName.get(row.tenant) ?? row.tenant} ${row.billing_name}`,
        size: 220,
        cell: ({ row }) => (
          <div className={styles.tenantCell}>
            <span>{tenantName.get(row.original.tenant) ?? row.original.tenant}</span>
            <span className={styles.small}>{row.original.billing_name}</span>
          </div>
        ),
      },
      {
        header: 'Status',
        accessorKey: 'status',
        size: 110,
        cell: ({ row }) => (
          <Chip label={statusLabel(row.original)} backgroundColour={statusColour(row.original)} />
        ),
      },
      {
        header: 'Issued',
        accessorKey: 'issued_on',
        size: 100,
        cell: ({ getValue }) => shortDate(getValue<string>()),
      },
      {
        header: 'Due',
        accessorKey: 'due_on',
        size: 100,
        cell: ({ getValue }) => shortDate(getValue<string>()),
      },
      {
        header: 'Period',
        accessorKey: 'period_start',
        size: 190,
        cell: ({ row }) =>
          `${shortDate(row.original.period_start)} to ${shortDate(row.original.period_end)}`,
      },
      {
        header: 'Total',
        accessorKey: 'total',
        size: 110,
        meta: { align: 'right' },
        cell: ({ row }) => fmtMoney(row.original.total, row.original.currency),
      },
      {
        header: 'Sent',
        accessorKey: 'sent_at',
        size: 120,
        cell: ({ getValue }) => {
          const value = getValue<string | null>();
          return value ? (
            new Date(value).toLocaleDateString('en-GB')
          ) : (
            <span className={styles.muted}>—</span>
          );
        },
      },
    ],
    [tenantName],
  );

  return (
    <div className={styles.page}>
      <header className={styles.header}>
        <div>
          <h1 className={styles.h1}>Invoices</h1>
          <p className={styles.subtitle}>
            {isLoading
              ? 'Loading…'
              : `${fmtMoney(owed)} outstanding across ${outstanding.length} invoice${
                  outstanding.length === 1 ? '' : 's'
                }${overdue.length ? `, ${fmtMoney(overdueOwed)} of it overdue` : ''}.`}
          </p>
        </div>
        <div className={styles.filters}>
          <div className={styles.segments} role="radiogroup" aria-label="Filter">
            {FILTERS.map((option) => (
              <button
                key={option.id}
                type="button"
                role="radio"
                aria-checked={filter === option.id}
                className={filter === option.id ? styles.segmentOn : styles.segment}
                onClick={() => setFilter(option.id)}
              >
                {option.label}
              </button>
            ))}
          </div>
          <input
            className={styles.search}
            type="search"
            placeholder="Search invoices"
            value={search}
            onChange={(event) => setSearch(event.target.value)}
            aria-label="Search invoices"
          />
        </div>
      </header>
      {error ? (
        <p className={styles.error}>
          Could not load invoices: {(error as { message?: string }).message ?? 'unknown error'}
        </p>
      ) : (
        <DataTable
          columns={columns}
          data={rows}
          storageKey="nexus-ui.invoices.table"
          globalFilter={search}
          onRowClick={(row) => navigate(`/tenants/${encodeURIComponent(row.tenant)}/invoices`)}
          emptyMessage={
            isLoading
              ? 'Loading invoices…'
              : 'No invoices match. Raise one from a tenant’s Invoices tab.'
          }
        />
      )}
      <CompanyDetails />
    </div>
  );
};
